import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { X } from "react-feather";
import { Button } from "@cinesys/ui/components";
import { SPOTTER_STRINGS } from "../strings";

/** Plain-language `when` condition examples for `ConditionSyntaxHelpModal` — deliberately NOT
 *  the xLucene/parser vocabulary (field/AST/node/regex, ...) the syntax is actually implemented
 *  with (see `spotterConditionEval.ts`): a customer building a rule just needs "type this to do
 *  that", not how it's evaluated. Every example is a real, currently-supported condition (mirrors
 *  a case in `spotterConditionEval.test.ts`) rather than an illustrative one that might not
 *  actually parse. */
const SYNTAX_EXAMPLES: { example: string; description: string }[] = [
  {
    example: "PlayType:Rush",
    description: 'Matches when PlayType is exactly "Rush".',
  },
  {
    example: "PlayType:Rush OR PlayType:Pass",
    description: "Matches if either value is set.",
  },
  {
    example: "Yards:>=10 AND PlayType:Rush",
    description: "Combine conditions with AND, OR, and NOT.",
  },
  {
    example: "NOT PlayType:Rush",
    description: "Matches everything except that value.",
  },
  {
    example: "(PlayType:Rush OR PlayType:Pass) AND Down:1",
    description: "Use parentheses to group conditions together.",
  },
  {
    example: "Yards:[0 TO 10]",
    description:
      "Matches a range of numbers. >=, <=, >, and < also work, e.g. Yards:>=10.",
  },
  {
    example: "PenaltyType:Hold*",
    description: 'The * matches any text, so this matches "Holding", too.',
  },
  {
    example: "_exists_:FrameioUrl",
    description:
      "Matches when that field has any value at all, set to anything.",
  },
];

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const Panel = styled.div`
  width: 100%;
  max-width: 520px;
  max-height: 80vh;
  overflow-y: auto;
  background: var(--spotterPaper);
  border: 1px solid var(--spotterLine);
  border-radius: 12px;
  box-shadow: var(--spotterShadow, 0 10px 30px rgba(0, 0, 0, 0.3));
  padding: 18px 20px;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 16px;
  color: var(--spotterInk);
`;

const Intro = styled.p`
  margin: 0 0 14px;
  font-size: 13.5px;
  color: var(--spotterInkSoft);
  line-height: 1.5;
`;

const ExampleRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 8px 0;

  & + & {
    border-top: 1px solid var(--spotterLine);
  }
`;

const Example = styled.code`
  font-family: ui-monospace, Menlo, monospace;
  font-size: 12.5px;
  color: var(--spotterFacial);
  word-break: break-word;
`;

const Description = styled.span`
  font-size: 13px;
  color: var(--spotterInkFaint);
  line-height: 1.4;
`;

interface ConditionSyntaxHelpModalProps {
  onClose: () => void;
}

/** A plain-language cheat sheet for the Conditional `when` field's condition syntax — a modal
 *  rather than a link to `xlucene-parser`'s own docs, which are written for implementing/
 *  extending the parser, not for a customer typing a one-line condition. */
const ConditionSyntaxHelpModal: React.FC<ConditionSyntaxHelpModalProps> = ({
  onClose,
}) => {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  return createPortal(
    <Overlay onClick={onClose}>
      <Panel onClick={(e) => e.stopPropagation()}>
        <PanelHeader>
          <Title>{SPOTTER_STRINGS.fields.conditional.syntaxHelpTitle}</Title>
          <Button icon={X} borderless onClick={onClose} tooltip="Close" />
        </PanelHeader>
        <Intro>{SPOTTER_STRINGS.fields.conditional.syntaxHelpIntro}</Intro>
        {SYNTAX_EXAMPLES.map(({ example, description }) => (
          <ExampleRow key={example}>
            <Example>{example}</Example>
            <Description>{description}</Description>
          </ExampleRow>
        ))}
      </Panel>
    </Overlay>,
    document.body
  );
};

export default ConditionSyntaxHelpModal;
